import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import {
  AUTOPILOT_CLONE_FLOW_QUERY_KEY,
  isTruthyAutopilotQueryValue,
} from '@/hooks/autopilot/shared';
import { ROUTE_ENTRY_QUERY_KEY, ROUTE_ENTRY_VALUES, isRouteEntry } from '@/lib/routeParams';
import { getStoredAuthToken, normalizeAuthToken } from '@/lib/authStorage';
import { getPendingSignupSuccessRoute } from '@/lib/authFlow';
import { useUserStore } from '@/store/useUserStore';

/**
 * 身份验证保护：仅允许登录用户访问
 * 如果未登录，重定向至登录页，并记录当前路径以便登录后返回
 */
export const RequireAuth = () => {
  const token = useUserStore((state) => state.token);
  const location = useLocation();
  const hasToken = Boolean(normalizeAuthToken(token) || getStoredAuthToken({ repair: false }));

  if (!hasToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
};

export const RequireAutopilotAccess = () => {
  const token = useUserStore((state) => state.token);
  const location = useLocation();
  const hasToken = Boolean(normalizeAuthToken(token) || getStoredAuthToken({ repair: false }));

  if (hasToken) {
    return <Outlet />;
  }

  const searchParams = new URLSearchParams(location.search);
  const isCloneGuestAccess =
    isTruthyAutopilotQueryValue(searchParams.get(AUTOPILOT_CLONE_FLOW_QUERY_KEY)) ||
    isRouteEntry(searchParams.get(ROUTE_ENTRY_QUERY_KEY), ROUTE_ENTRY_VALUES.cloneMigration);
  const isPrelaunchGuestAccess = isTruthyAutopilotQueryValue(
    searchParams.get('autopilotPrelaunch'),
  );

  if (isCloneGuestAccess || isPrelaunchGuestAccess) {
    return <Outlet />;
  }

  return <Navigate to="/login" state={{ from: location }} replace />;
};

/**
 * 反向身份验证保护：仅允许未登录用户访问（如登录、注册页）
 * 如果已登录，重定向至登录前的页面或首页
 */
export const RequireGuest = () => {
  const token = useUserStore((state) => state.token);
  const location = useLocation();
  const hasToken = Boolean(normalizeAuthToken(token) || getStoredAuthToken({ repair: false }));
  const isLoggedIn = hasToken;
  const pendingSignupSuccessRoute = getPendingSignupSuccessRoute();
  const isOAuthCallback = location.pathname === '/oauth/callback';

  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect');
  const shouldRedirectExternal =
    !isOAuthCallback && isLoggedIn && !!redirectUrl && !pendingSignupSuccessRoute;

  useEffect(() => {
    if (isOAuthCallback) return;
    if (shouldRedirectExternal && redirectUrl) {
      window.location.href = redirectUrl;
    }
  }, [isOAuthCallback, shouldRedirectExternal, redirectUrl]);

  if (isOAuthCallback) {
    return <Outlet />;
  }

  if (isLoggedIn) {
    if (pendingSignupSuccessRoute) {
      return <Navigate to={pendingSignupSuccessRoute} replace />;
    }

    if (shouldRedirectExternal) {
      return null;
    }

    // 优先跳转回登录前的页面，保留完整的路径、搜索参数和 hash
    const from = (location.state as { from?: { pathname: string; search?: string; hash?: string } })
      ?.from || { pathname: '/' };
    const to = `${from.pathname}${from.search || ''}${from.hash || ''}`;
    return <Navigate to={to} replace />;
  }

  return <Outlet />;
};
